/**
 * Video component module.
 * @module Video
 */

const newsVjVideoEmbedURI = '//news.files.bbci.co.uk/include/datapres/idt/news-vj-video-embed-iframe/1.0.2/js/host.js';

function loadNewsVjVideoEmbed({ onload }) {
  // eslint-disable-next-line no-undef
  if (window.newsVjVideoEmbed) {
    onload();
    return;
  }
  // eslint-disable-next-line no-undef
  const scriptTag = document.createElement('script');
  scriptTag.type = 'text/javascript';
  scriptTag.addEventListener('load', onload);
  scriptTag.src = newsVjVideoEmbedURI;
  // eslint-disable-next-line no-undef
  document.body.appendChild(scriptTag);
}

/**
 * Initialize Video component.
 * @alias module:Video
 *
 * @param {object} videoInfo - Arguments to be passed to `window.newsVjVideoEmbed.addVideo`.
 * @param {HTMLDocument | ShadowRoot} [videoInfo.scope=document] - Component scope.
 * @param {string} videoInfo.vpid - Version PID or Stream ID (service ID). This must be a version pid (e.g.
 *     [p04qhgkp]{@link https://api.live.bbc.co.uk/pips/inspector/pip/p04qhgkp/}) rather than a clip
 *     pid (e.g. [p04qhgkh]{@link https://api.live.bbc.co.uk/pips/inspector/pip/p04qhgkh/}). You
 *     may be able to use the [PIPS inspector]{@link https://api.live.bbc.co.uk/pips/inspector/} or
 *     [IDT Video Embed Tool]{@link https://idt.vj.tools.bbc.co.uk/video-embed-tool/} to convert
 *     to the pid you need.
 * @param {string} [videoInfo.title] - Title of the video.
 *     Only define this if you have to override `title` from handlebars.
 * @param {string} [videoInfo.poster_image] - Holding image to be displayed before hitting 'Play'.
 *     Only define this if you have to override `poster-image` from handlebars.
 * @param {bool} [videoInfo.autoplay=false] - Whether or not to autoplay.
 * @param {bool} [videoInfo.include_preroll=false] - Whether or not to include preroll.
 * @param {bool} [videoInfo.include_analytics=false] - Whether or not to include analytics.
 * @param {bool} [videoInfo.responsive=true] - Whether or not to use responsive video player.
 * @param {bool} [videoInfo.threeSixty=false] - Whether or not the source is 360 video.
 * @param {string} [videoInfo.kind='programme'] - The type of the content (e.g. 'programme', 'audio', '360',
 * etc.). See [things to play]{@link https://confluence.dev.bbc.co.uk/display/mp/Loading+a+Playlist+with+an+object#LoadingaPlaylistwithanobject-kinds}
 *
 * @see [news-vj-video-embed-iframe]{@link https://github.com/bbc/news-vj-video-embed-iframe} for
 *     valid paramenters.
 * @see [Confluence page about SMP player.]{@link https://confluence.dev.bbc.co.uk/display/mp/Loading+a+Playlist+with+an+object}
 * @see [Sample arguments to play 360 video.]{@link https://github.com/bbc/newsspec-18675-360-video/blob/master/source/app.hbs#L4-L13}
 */
export default function Video(videoInfo) {
  // eslint-disable-next-line no-undef
  const { scope = document, vpid } = videoInfo;
  const container = scope.querySelector(`#video-embed__${vpid}`);
  const title = container.getAttribute('data-title');
  const posterImage = container.getAttribute('data-poster-image');
  loadNewsVjVideoEmbed({
    scope,
    onload: () => {
      // eslint-disable-next-line no-undef
      window.newsVjVideoEmbed.addVideo(Object.assign({
        container,
        vpid,
        title,
        poster_image: posterImage,
      }, videoInfo));
    },
  });
}
